import { motion } from 'framer-motion'
import { Link, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { springLayout } from '../../design/motion'
import { groupForPath, routeForGroup, type NavGroup, type NavGroupId } from './navGroups'
import { NavGlyph } from './NavGlyph'
import { NavDot, type DotKind } from './NavDot'

interface Props {
  groups: NavGroup[]
  gymSlug: string | null
  /**
   * Tabs carrying something worth a look, and what each one means: an
   * incomplete profile, or news and events you have not seen. A dot rather
   * than a number — a thumb reaching for a tab is deciding whether to go
   * there, not working through a queue.
   */
  dots?: Partial<Record<NavGroupId, DotKind | false>>
}

/**
 * The phone's primary navigation.
 *
 * Fixed to the bottom edge because that is where a thumb is while you are
 * standing under a wall, which is the whole reason the hamburger drawer this
 * replaces was the worst part of the app.
 */
export function BottomTabBar({ groups, gymSlug, dots }: Props) {
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const active = groupForPath(pathname)

  return (
    <nav
      aria-label={t('nav.openMenu')}
      className="border-t border-[var(--gym-line)] bg-[var(--gym-bar)]/95 pb-[var(--safe-b)] backdrop-blur-xl"
    >
      <ul className="flex items-stretch justify-around">
        {groups.map((group) => {
          const to = routeForGroup(group, gymSlug)
          if (!to) return null
          const isActive = active === group.id
          const dot = dots?.[group.id] ?? false

          return (
            <li key={group.id} className="flex-1">
              <Link
                to={to}
                aria-current={isActive ? 'page' : undefined}
                className="relative flex flex-col items-center gap-1 px-1 pb-2 pt-2.5"
              >
                {isActive && (
                  <motion.span
                    layoutId="tab-indicator"
                    transition={springLayout}
                    aria-hidden
                    className="absolute inset-x-3 top-0 h-0.5 rounded-full bg-[var(--gym-primary)]"
                  />
                )}
                <span className="relative">
                  <NavGlyph
                    group={group.id}
                    className={`h-[22px] w-[22px] transition-colors ${
                      isActive ? 'text-[var(--gym-primary)]' : 'text-foreground/45'
                    }`}
                  />
                  {dot && (
                    <NavDot
                      kind={dot}
                      size="lg"
                      className="absolute -right-1.5 -top-1 ring-2 ring-[var(--gym-bar)]"
                    />
                  )}
                </span>
                <span
                  className={`text-[11px] leading-none transition-colors ${
                    isActive ? 'font-semibold text-foreground' : 'font-medium text-foreground/45'
                  }`}
                >
                  {t(group.labelKey)}
                </span>
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
